import { useCallback } from 'react';

import type { TabBarActionButtonProps } from './TabBarActionButton.types';

/**
 * A large, protruding center action button for the bottom tab bar.
 *
 * Features:
 * - 56px diameter circle with sakura-to-petal gradient background
 * - Protrudes 16px above the tab bar
 * - 4px "cutout" shadow matching tab bar background (seamless floating effect)
 * - Subtle glow shadow using primary-300
 * - Hover: lifts + scales
 * - Active: presses down
 *
 * @example
 * ```tsx
 * <TabBarActionButton
 *   icon={<BoltIcon className="h-6 w-6" />}
 *   onClick={() => navigate('/start-workout')}
 *   ariaLabel="Start Workout"
 * />
 * ```
 */
export function TabBarActionButton({
  icon,
  onClick,
  ariaLabel,
  className = '',
}: TabBarActionButtonProps) {
  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  // The floating action button uses bespoke compound shadows (drop shadow + ring)
  // whose sakura alpha values (0.3 / 0.4) have no named token — shadow-sakura-glow
  // is a different single-layer glow. Kept as a documented one-off.
  /* eslint-disable do-tokens/no-hardcoded-color -- bespoke compound sakura action-button shadow; alphas have no token */
  const buttonClass = `
    relative -top-4 flex items-center justify-center
    h-14 w-14
    rounded-full
    bg-gradient-bloom
    shadow-[0_4px_12px_rgba(240,171,252,0.3),0_0_0_4px_var(--color-contrast-700)]
    transition-all duration-normal ease-spring
    hover:-translate-y-1 hover:scale-105
    hover:shadow-[0_6px_20px_rgba(240,171,252,0.4),0_0_0_4px_var(--color-contrast-700)]
    active:translate-y-0 active:scale-95
    active:shadow-[0_2px_8px_rgba(240,171,252,0.3),0_0_0_4px_var(--color-contrast-700)]
    focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-400
    [&>svg]:h-[26px] [&>svg]:w-[26px] [&>svg]:text-contrast-950
  `;
  /* eslint-enable do-tokens/no-hardcoded-color */

  return (
    <div className={`flex flex-1 items-end justify-center ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={buttonClass}
      >
        {icon}
      </button>
    </div>
  );
}
